import { linkOptions } from "@tanstack/react-router";
import type { CSSProperties } from "react";
import { isIOS, isMobileSafari, isSafari } from "react-device-detect";

import { chatRoomPath, chatSpacePath } from "@/react/helpers/urlHelpers";
import type { ChatRoom } from "@/react/types/Chat/ChatRoom";
import type { ChatRoomMessage } from "@/react/types/Chat/ChatRoomMessage";
import type { CurrentContact } from "@/react/types/CurrentContact";

export const getCopyLinkUrl = ({
  id,
  parentMessageId,
  chatRoomUrl,
}: {
  id: number;
  parentMessageId?: number | null;
  chatRoomUrl?: string;
}) => {
  const hostUrl =
    window.location.protocol +
    "//" +
    window.location.host +
    (chatRoomUrl || window.location.pathname);
  return (
    hostUrl +
    (parentMessageId
      ? `?message_id=${id}#message_${parentMessageId}`
      : `#message_${id}`)
  );
};

export const doesActiveMessageIdMatches = ({
  activeMessageId,
  id,
  isMessageThread = false,
}: {
  activeMessageId: string | null;
  id: number;
  isMessageThread: boolean;
}) => activeMessageId === getActiveMessageId(id, isMessageThread);

export const getActiveMessageId = (id: number, isMessageThread: boolean) =>
  `${isMessageThread ? "thread-" : "message-"}${String(id)}`;

export const getChatRoomUrlWithMessageId = (
  chatRoom: {
    slug: string;
    uuid: string;
  },
  message: Pick<ChatRoomMessage, "id" | "parent_message_id"> & {
    embedded?: ChatRoomMessage["embedded"];
  },
) => {
  const { slug, uuid } = chatRoom;
  const {
    id,
    parent_message_id: parentMessageId,
    embedded: isEmbedded,
  } = message;

  const chatRoomUrl = isEmbedded
    ? chatSpacePath({ spaceSlug: slug })
    : chatRoomPath({ uuid });

  let messageParams = `#message_${id}`;
  if (parentMessageId) {
    messageParams = `?message_id=${id}#message_${parentMessageId}`;
  }

  return `${chatRoomUrl}${messageParams}`;
};

export const getChatRoomLinkOptionsWithMessageId = (
  chatRoom: {
    slug: string;
    uuid: string;
  },
  message: Pick<ChatRoomMessage, "id" | "parent_message_id"> & {
    embedded?: ChatRoomMessage["embedded"];
  },
) => {
  const { slug, uuid } = chatRoom;
  const {
    id,
    parent_message_id: parentMessageId,
    embedded: isEmbedded,
  } = message;

  return isEmbedded
    ? linkOptions({
        to: "/c/$spaceSlug",
        params: { spaceSlug: slug },
        hash: parentMessageId ? `message_${parentMessageId}` : `message_${id}`,
        search: parentMessageId ? { message_id: id } : undefined,
      })
    : linkOptions({
        to: "/messages/$uuid",
        params: { uuid },
        hash: parentMessageId ? `message_${parentMessageId}` : `message_${id}`,
        search: parentMessageId ? { message_id: id } : undefined,
      });
};

let tempMessageId = 0;
const nextTempMessageId = () => {
  tempMessageId--;
  return tempMessageId;
};

export const createTempChatRoomMessage = ({
  chatRoom,
  currentContact,
  richTextBody,
  localAttachments,
  localSgidToObjectMap,
  parentMessageId,
  creationUuid,
  sentAt,
  unfurlUrls,
}: {
  chatRoom: ChatRoom;
  currentContact?: CurrentContact | null;
  richTextBody: any;
  localAttachments: any;
  localSgidToObjectMap: any;
  parentMessageId?: number;
  creationUuid: string;
  sentAt: string;
  unfurlUrls?: { [key: string]: boolean };
}) => {
  const currentParticipant = chatRoom.current_participant;
  const currentParticipantId = currentParticipant?.id;
  if (!currentParticipant || !currentParticipantId) {
    return null;
  }
  const tempMessage: ChatRoomMessage = {
    id: nextTempMessageId(),
    chat_room_id: chatRoom.id,
    rich_text_body: {
      ...richTextBody,
      attachments: localAttachments,
      sgids_to_object_map: localSgidToObjectMap,
    },
    body: "",
    chat_room_uuid: chatRoom.uuid,
    chat_room_participant_id: currentParticipantId,
    creation_uuid: creationUuid,
    sender: {
      id: currentParticipantId,
      community_member_id: currentParticipant.community_member_id,
      contact_id: currentParticipant.contact_id,
      user_public_uid: currentParticipant.user_public_uid,
      name: currentContact?.name ?? currentParticipant.name,
      avatar_url: currentContact?.avatar_url ?? currentParticipant.avatar_url,
      is_guest: currentParticipant.is_guest,
      community_admin: currentParticipant.community_admin,
      moderator: currentParticipant.admin,
    },
    reactions: [],
    parent_message_id: parentMessageId ?? null,
    updated_at: sentAt,
    sent_at: sentAt,
    created_at: sentAt,
    chat_thread_id: null,
  };

  if (unfurlUrls) {
    tempMessage.unfurl_urls = unfurlUrls;
  }

  if (parentMessageId) {
    tempMessage.parent_message_id = parentMessageId;
  }

  return tempMessage;
};

const getIntrinsicSize = (message: ChatRoomMessage) => {
  const { rich_text_body: richTextBody, replies_count: repliesCount = 0 } =
    message;
  const hasAttachments = richTextBody?.attachments?.length > 0;
  const hasReplies = repliesCount > 0;

  if (hasAttachments) {
    return "auto 281px"; // Messages with images
  }
  if (hasReplies) {
    return "auto 120px"; // Messages with replies block
  }
  return "auto 65px"; // Simple text messages
};

// WebKit's `content-visibility: auto` implementation fails to paint subtrees
// whose real height greatly exceeds the intrinsic placeholder (e.g. large
// messages with attachments), leaving the chat blank. This affects Safari and
// every iOS browser (all forced onto WebKit), so skip the optimization there
// and fall back to normal rendering.
//
// It is also skipped while a message is being edited: `content-visibility`
// implies paint containment, which makes the wrapper the containing block for
// fixed-position descendants and clips them, hiding the inline editor's
// TextModifiers bubble menu.
export const getMessageContentVisibilityStyle = (
  message: ChatRoomMessage,
  isEditMessage = false,
): CSSProperties => {
  if (isSafari || isMobileSafari || isIOS || isEditMessage) {
    return {};
  }

  return {
    contentVisibility: "auto",
    containIntrinsicBlockSize: getIntrinsicSize(message),
  };
};
